import { useCallback, useMemo, useState } from 'react';
import { EventSearch } from 'src/components/Events/EventSearch/EventSearch';
import { EventSearchResult } from 'src/components/Events/EventSearch/EventSearch.types';
import { Content } from 'src/contexts/ContentContext';
import { Button, Stack } from 'src/core/ui';
import { EventEntityHeader } from 'src/modals/common/EventEntityHeader';
import { Modal } from 'src/modals/Modal';
import { ModalBodyDataContainer } from 'src/modals/Modal/Modal.styled';
import { ContentId } from 'src/utils/constants/contentId';
import { Performer, Venue } from 'src/WebApiController';

import { AllocationSubmissionStep } from './AllocationSubmissionStep';

// ─── Types ───────────────────────────────────────────────────────────────────

export type AllocatePlaceholderEventsFlowProps = {
  isOpen: boolean;
  onClose: () => void;
  onAllocationDone: () => void;
  posEventIdsToAllocate: string[];
  /** Full venue object for the placeholder group — used to pre-populate the venue filter chip. */
  venue?: Venue | null;
  /** Full performer object for the placeholder group — used to pre-populate the performer filter chip. */
  performer?: Performer | null;
};

type Step = 'eventSearch' | 'createAllocations';

// ─── Component ───────────────────────────────────────────────────────────────

export function AllocatePlaceholderEventsFlow({
  isOpen,
  onClose,
  posEventIdsToAllocate,
  onAllocationDone,
  venue,
  performer,
}: AllocatePlaceholderEventsFlowProps) {
  const [step, setStep] = useState<Step>('eventSearch');
  const [selectedEvents, setSelectedEvents] = useState<EventSearchResult[]>([]);

  const [isSubmissionComplete, setIsSubmissionComplete] = useState(false);

  const unallocatedSlotsCount = useMemo(
    () => posEventIdsToAllocate.length,
    [posEventIdsToAllocate.length]
  );

  const resetState = useCallback(() => {
    setStep('eventSearch');
    setSelectedEvents([]);
    setIsSubmissionComplete(false);
  }, []);

  const handleClose = useCallback(() => {
    onClose();
    resetState();
  }, [onClose, resetState]);

  const handleComplete = useCallback(() => {
    onAllocationDone();
    resetState();
  }, [onAllocationDone, resetState]);

  const handleEventsSelect = useCallback(
    (results: EventSearchResult[]) => {
      setSelectedEvents((prev) => {
        const next = [...prev];
        for (const result of results) {
          const existingIndex = next.findIndex(
            (e) => e.event.virtId === result.event.virtId
          );
          if (result.isSelected) {
            if (existingIndex === -1) {
              if (next.length >= unallocatedSlotsCount) {
                continue;
              }
              next.push(result);
            } else {
              next[existingIndex] = result;
            }
          } else {
            if (existingIndex !== -1) {
              next.splice(existingIndex, 1);
            }
          }
        }
        return next;
      });
    },
    [unallocatedSlotsCount]
  );

  const selectedEventVirtualIds = selectedEvents.map((e) => e.event.virtId);

  // ─── Shared header ───────────────────────────────────────────────────────

  const header = (
    <EventEntityHeader title={<Content id={ContentId.AllocateEvents} />} />
  );

  // ─── Step bodies ─────────────────────────────────────────────────────────

  const eventSearchBody = (
    <ModalBodyDataContainer>
      <EventSearch
        selectedEvents={selectedEventVirtualIds}
        onEventsSelect={handleEventsSelect}
        numEventsSelected={selectedEvents.length}
        onDeleteNumEventsSelected={() => setSelectedEvents([])}
        showEventsToSearchFromOnEmptySearch
        defaultSearchEventsQuery={{
          venueId: venue?.viagId ?? null,
          venue: venue ?? null,
          performerId: performer?.viagId ?? null,
          performer: performer ?? null,
          searchText: null,
          timeFrame: null,
        }}
        maxSelections={unallocatedSlotsCount}
      />
    </ModalBodyDataContainer>
  );

  // ─── Step footers ────────────────────────────────────────────────────────

  const eventSearchFooter = (
    <Stack
      direction="row"
      justifyContent="end"
      width="full"
      alignItems="center"
    >
      <Button
        data-testid="next-button"
        variant="regular"
        onClick={() => setStep('createAllocations')}
        disabled={selectedEvents.length === 0}
      >
        <Content id={ContentId.Next} />
      </Button>
    </Stack>
  );

  const submissionFooter = (
    <Stack
      direction="row"
      justifyContent="end"
      width="full"
      alignItems="center"
    >
      <Button
        variant="regular"
        onClick={handleClose}
        disabled={!isSubmissionComplete}
      >
        <Content id={ContentId.Close} />
      </Button>
    </Stack>
  );

  const steps: Record<Step, JSX.Element> = {
    eventSearch: eventSearchBody,
    createAllocations: (
      <AllocationSubmissionStep
        selectedEvents={selectedEvents}
        posEventIdsToAllocate={posEventIdsToAllocate}
        onComplete={handleComplete}
      />
    ),
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="slide-in"
      scrollable
      Header={header}
      Body={steps[step]}
      Footer={step === 'eventSearch' ? eventSearchFooter : submissionFooter}
    />
  );
}
